import { useState, useEffect } from "react"
import { useToast } from "@crm-src/hooks/use-toast"
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material"
import { customerContactsApi, type CustomerContactCreateData } from "@crm-src/lib/customer-contacts-api"

export interface ContactFormData extends CustomerContactCreateData {}

interface ContactFormModalProps {
  isOpen: boolean
  onClose: () => void
  customerId: string
  contactToEdit?: any | null  // If provided, edit mode
  onContactSaved: () => void
}

export function ContactFormModal({
  isOpen,
  onClose,
  customerId,
  contactToEdit,
  onContactSaved
}: ContactFormModalProps) {
  const { toast } = useToast()
  const [isSaving, setIsSaving] = useState(false)
  const isEditMode = !!contactToEdit
  
  const [formData, setFormData] = useState<ContactFormData>({
    first_name: "",
    last_name: "",
    email: "",
    email2: "",
    phone: "",
    phone2: "",
    mobile: "",
    company_name: "",
    job_title: "",
    website: "",
    timezone: "",
    address: "",
    city: "",
    state: "",
    country: "",
    postal_code: "",
  })

  // Populate form when contact or modal opens
  useEffect(() => {
    if (isOpen) {
      if (contactToEdit) {
        // Edit mode - populate with existing data
        setFormData({
          first_name: contactToEdit.first_name || "",
          last_name: contactToEdit.last_name || "",
          email: contactToEdit.email || "",
          email2: contactToEdit.email2 || "",
          phone: contactToEdit.phone || "",
          phone2: contactToEdit.phone2 || "",
          mobile: contactToEdit.mobile || "",
          company_name: contactToEdit.company_name || "",
          job_title: contactToEdit.job_title || "",
          website: contactToEdit.website || "",
          timezone: contactToEdit.timezone || "",
          address: contactToEdit.address || "",
          city: contactToEdit.city || "",
          state: contactToEdit.state || "",
          country: contactToEdit.country || "",
          postal_code: contactToEdit.postal_code || "",
        })
      } else {
        // Create mode - reset form
        resetForm()
      }
    }
  }, [contactToEdit, isOpen])

  const resetForm = () => {
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      email2: "",
      phone: "",
      phone2: "",
      mobile: "",
      company_name: "",
      job_title: "",
      website: "",
      timezone: "",
      address: "",
      city: "",
      state: "",
      country: "",
      postal_code: "",
    })
  }

  const handleSave = async () => {
    try {
      setIsSaving(true)

      // Clean up form data - convert empty strings to empty string (Rails will handle as nil)
      // but we need to ensure whitespace-only strings are treated as empty
      const cleanedData = Object.entries(formData).reduce((acc, [key, value]) => {
        (acc as any)[key] = (typeof value === 'string' && value.trim() === '') ? '' : value
        return acc
      }, {} as ContactFormData)

      if (isEditMode && contactToEdit) {
        // Update existing contact
        await customerContactsApi.update(customerId, contactToEdit.id, cleanedData)
        toast({
          title: "Success",
          description: "Contact updated successfully",
        })
      } else {
        // Create new contact
        await customerContactsApi.create(customerId, cleanedData)
        toast({
          title: "Success",
          description: "Contact created successfully",
        })
      }

      onContactSaved()
      onClose()
      resetForm()
    } catch (error) {
      console.error('Error saving contact:', error)
      toast({
        title: "Error",
        description: `Failed to ${isEditMode ? 'update' : 'create'} contact`,
        variant: "destructive",
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleClose = () => {
    if (!isSaving) {
      onClose()
      resetForm()
    }
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { maxWidth: 600 } }}>
      <DialogTitle sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {isEditMode ? 'Edit Contact' : 'Add New Contact'}
        <Typography sx={{ fontSize: 14, color: "text.secondary", fontWeight: 400 }}>
          {isEditMode ? 'Update contact information.' : 'Add a new contact for this customer.'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, py: 2 }}>
          <TextField
            id="first_name"
            label="First Name"
            size="small"
            fullWidth
            value={formData.first_name || ""}
            onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
            placeholder="John"
            autoComplete="given-name"
          />
          <TextField
            id="last_name"
            label="Last Name"
            size="small"
            fullWidth
            value={formData.last_name || ""}
            onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
            placeholder="Doe"
            autoComplete="family-name"
          />
          <TextField
            id="email"
            label="Email"
            type="email"
            size="small"
            fullWidth
            value={formData.email || ""}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="john@company.com"
            autoComplete="email"
          />
          <TextField
            id="email2"
            label="Email 2"
            type="email"
            size="small"
            fullWidth
            value={formData.email2 || ""}
            onChange={(e) => setFormData({ ...formData, email2: e.target.value })}
            placeholder="john.doe@personal.com"
            autoComplete="email"
          />
          <TextField
            id="phone"
            label="Phone"
            size="small"
            fullWidth
            value={formData.phone || ""}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="+1 (555) 123-4567"
            autoComplete="tel"
          />
          <TextField
            id="phone2"
            label="Phone 2"
            size="small"
            fullWidth
            value={formData.phone2 || ""}
            onChange={(e) => setFormData({ ...formData, phone2: e.target.value })}
            placeholder="+1 (555) 987-6543"
            autoComplete="tel"
          />
          <TextField
            id="mobile"
            label="Mobile"
            size="small"
            fullWidth
            value={formData.mobile || ""}
            onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
            placeholder="+1 (555) 987-6543"
            autoComplete="tel"
          />
          <TextField
            id="company_name"
            label="Company"
            size="small"
            fullWidth
            value={formData.company_name || ""}
            onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
            placeholder="Company Name"
            autoComplete="organization"
          />
          <TextField
            id="job_title"
            label="Job Title"
            size="small"
            fullWidth
            value={formData.job_title || ""}
            onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
            placeholder="CEO, CTO, etc."
            autoComplete="organization-title"
          />
          <TextField
            id="website"
            label="Website"
            size="small"
            fullWidth
            value={formData.website || ""}
            onChange={(e) => setFormData({ ...formData, website: e.target.value })}
            placeholder="https://company.com"
            autoComplete="url"
          />
          <TextField
            id="timezone"
            label="Timezone"
            size="small"
            fullWidth
            value={formData.timezone || ""}
            onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
            placeholder="America/New_York"
            autoComplete="off"
          />
          <TextField
            id="address"
            label="Street Address"
            size="small"
            fullWidth
            value={formData.address || ""}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            placeholder="123 Main Street"
            autoComplete="street-address"
            sx={{ gridColumn: "span 2" }}
          />
          <TextField
            id="city"
            label="City"
            size="small"
            fullWidth
            value={formData.city || ""}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            placeholder="New York"
            autoComplete="address-level2"
          />
          <TextField
            id="state"
            label="State/Province"
            size="small"
            fullWidth
            value={formData.state || ""}
            onChange={(e) => setFormData({ ...formData, state: e.target.value })}
            placeholder="NY"
            autoComplete="address-level1"
          />
          <TextField
            id="country"
            label="Country"
            size="small"
            fullWidth
            value={formData.country || ""}
            onChange={(e) => setFormData({ ...formData, country: e.target.value })}
            placeholder="United States"
            autoComplete="country-name"
          />
          <TextField
            id="postal_code"
            label="Postal Code"
            size="small"
            fullWidth
            value={formData.postal_code || ""}
            onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
            placeholder="10001"
            autoComplete="postal-code"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleClose}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving}
          {...(isSaving ? { startIcon: <CircularProgress size={16} color="inherit" /> } : {})}
        >
          {isSaving
            ? (isEditMode ? "Updating..." : "Creating...")
            : (isEditMode ? "Update Contact" : "Add Contact")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

